import React, { useEffect, useState } from "react";
import styled from "styled-components";
import PropTypes from "prop-types";
import BaseTemplate from "./BaseTemplate"; // Import your BaseTemplate
import Icon from "new_components/atoms/Icon";
import { ICON_SET } from "new_components/atoms/Icon/IconSet";
import colors from "styles/colors";
// Using semantic tooltip for these buttons due to complexity.
import { Popup } from "semantic-ui-react";

const LayoutWrapper = styled.div`
  display: flex;
  flex: ${({ chatStarted }) => (chatStarted ? "none" : "1")};
  height: ${({ chatStarted }) => (chatStarted ? "100%" : "calc(100vh - 40px)")};
  position: relative;
`;

const SidebarWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  width: 250px;
  background-color: #fff;
  border-right: ${({ isOpen }) => (isOpen ? "1px solid #ddd" : "none")};
  overflow-y: hidden;
  overflow-x: hidden;
  transform: translateX(${({ isOpen }) => (isOpen ? "205px" : "-150%")});
  transition: transform 0.3s ease, border-right 0.2s ease;
  padding-left: 5px;
  padding-right: 10px;

  &:hover {
    overflow-y: auto; /* Show scroll on hover */
  }

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 10px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #999;
  }
  z-index: 21;
`;

const ToggleButton = styled.div`
  position: fixed;
  top: 16px;
  left: 210px;
  background-color: #fff;
  border-radius: 5px;
  width: 30px;
  height: 30px;
  cursor: pointer;
  z-index: 1000;

  display: flex;
  align-items: center;
  justify-content: center;

  transition: transform 0.3s ease, background-color 0.2s ease;
  &:hover {
    background-color: #ddd;
    border: 1px solid #ddd;
  }
`;

const NewChatButton = styled.div`
  position: fixed;
  top: 16px;
  left: ${({ isOpen }) => (isOpen ? "400px" : "250px")};
  background-color: #fff;
  border-radius: 5px;
  width: 30px;
  height: 30px;
  cursor: pointer;
  z-index: 1000;

  transition: left 0.3s ease, background-color 0.2s ease;

  display: flex;
  align-items: center;
  justify-content: center;
  &:hover {
    background-color: #ddd;
    border: 1px solid #ddd;
  }
`;

const SidebarContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const SubmenuSection = styled.div`
  height: 100%;
  border-radius: 4px;
  padding-top: 10px;
`;

const ContentWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
  transform: translateX(${({ isOpen }) => (isOpen ? "150px" : "0")});
  transition: transform 0.3s ease;
  position: relative;
`;

const ChatContent = styled.div`
  flex-direction: column;
  gap: 20px;
  width: 100%;
  height: 100%;
  padding-bottom: 100px;
`;

const ChatHeading = styled.div`
  display: ${({ chatStarted }) => (chatStarted ? "none" : "flex")};
  flex-direction: column;
  align-items: center;
  gap: 20px;
`;

const InputBox = styled.div`
  position: ${({ chatStarted }) => (chatStarted ? "fixed" : "fixed")};
  bottom: ${({ chatStarted }) => (chatStarted ? "0" : "unset")};
  top: ${({ chatStarted }) => (chatStarted ? "unset" : "300px")};

  /* Determine left based on chatStarted and sidebarOpen */
  left: ${({ chatStarted, sidebarOpen }) =>
    !chatStarted
      ? sidebarOpen
        ? "calc(50% + 250px)"
        : "calc(50% + 100px)"
      : sidebarOpen
      ? "calc((100vw + 500px) / 2)"
      : "calc((100vw + 225px) / 2)"};

  width: 75%;
  max-width: 800px;
  padding: 16px;
  /* When a chat hasn't started, use 50px margin below the header.
     When the chat starts, revert to the old margin (40px) */
  margin-top: ${({ chatStarted }) => (chatStarted ? "40px" : "50px")};
  display: flex;
  justify-content: center;
  flex-direction: column;
  box-sizing: border-box;
  transform: translateX(-50%);
  transition: left 0.3s ease;
  z-index: 50;
`;

const OverlayHide = styled.div`
  height: 130px;
  width: 100%;
  position: fixed;
  background-color: #fff;
  bottom: 0;
  z-index: 1;
`;

const ChatBox = styled.div`
  position: ${({ chatStarted }) => (chatStarted ? "relative" : "fixed")};
  bottom: ${({ chatStarted }) => (chatStarted ? "150px" : "250px")};
  left: ${({ chatStarted, sidebarOpen }) =>
    chatStarted
      ? sidebarOpen
        ? "150px"
        : "0px"
      : sidebarOpen
      ? "250px"
      : "100px"};
  width: ${({ chatStarted }) => (chatStarted ? "calc(100% - 60px)" : "100%")};
  transition: left 0.3s ease;
  display: flex;
  justify-content: center;
  padding: 16px;
  box-sizing: border-box;

  transform: translateX(${({ isOpen }) => (isOpen ? "150px" : "0")});
`;

const ChatScrollArea = styled.div`
  flex: 1;
  padding: 16px;
  scroll-behavior: smooth;
  overflow-y: auto;
  height: 100%;
  margin-top: 0;
`;

const ScrollToBottomButton = styled.div`
  position: fixed;
  bottom: 150px;
  right: ${({ sidebarOpen }) => (sidebarOpen ? "calc(50% - 250px)" : "calc(50% - 100px)")};
  background-color: white;
  color: ${colors.primary};
  border-radius: 50%;
  width: 40px;
  height: 40px;
  cursor: pointer;
  z-index: 1000;
  display: ${({ show }) => (show ? "flex" : "none")};
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
  opacity: 1;
  &:hover {
    transform: scale(1.05);
  }
`;

const ChatTemplate = ({
  heading,
  pageTitle,
  subtitle,
  scrollable,
  chatHeading,
  chatContent,
  chatStarted,
  chatComponent,
  submenuData, // { sectionOne: JSX, sectionTwo: JSX }
  activeRoom,
  startNewChat,
  children,
  inputBox,
  modals,
  fromPulseSurvey,
}) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(!fromPulseSurvey);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);

  const toggleSidebar = () => {
    setIsSidebarOpen((prev) => !prev);
  };

  useEffect(() => {
    if (chatStarted) {
      setIsSidebarOpen(false);
      setShowScrollToBottom(false);
    }
  }, [activeRoom, chatStarted]);

  useEffect(() => {
    // if new chat is clicked, reset the scroll to bottom button
    setShowScrollToBottom(false);
  }, [chatStarted]);

  useEffect(() => {
    const onScroll = () => {
      const scrollTop = document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = document.documentElement.clientHeight;

      const atBottom = scrollTop + clientHeight >= scrollHeight - 5;

      // Show scroll-to-bottom button when NOT at bottom and chat has started
      setShowScrollToBottom(!atBottom && chatStarted);
    };

    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [chatStarted]);

  const scrollToBottom = () => {
    window.scrollTo({
      top: document.documentElement.scrollHeight,
      behavior: "smooth"
    });
  };


  return (
    <BaseTemplate
      heading={!chatStarted && heading}
      pageTitle={!chatStarted && pageTitle}
      subtitle={!chatStarted && subtitle}
      scrollable={chatStarted ? false : scrollable}
      chatStarted={chatStarted}
    >
      {modals}
      <LayoutWrapper chatStarted={chatStarted}>
        <Popup
          content="Toggle history"
          inverted
          position="bottom left"
          trigger={
            <ToggleButton onClick={toggleSidebar} isOpen={isSidebarOpen}>
              <Icon
                name={ICON_SET.columns}
                style={{ cursor: "pointer" }}
                size={18}
              />
            </ToggleButton>
          }
        />

        <Popup
          content="New Chat"
          inverted
          position="bottom left"
          trigger={
            <NewChatButton
              isOpen={isSidebarOpen}
              onClick={() => startNewChat()}
            >
              <Icon
                name={ICON_SET.editIcon2}
                size={18}
                style={{ cursor: "pointer" }}
              />
            </NewChatButton>
          }
        />

        <SidebarWrapper isOpen={isSidebarOpen}>
          <SidebarContent>
            <SubmenuSection>
              {submenuData?.sectionOne /* Render first section */}
            </SubmenuSection>
          </SidebarContent>
        </SidebarWrapper>

        <ContentWrapper chatStarted={chatStarted} isOpen={isSidebarOpen}>
          <ChatScrollArea chatStarted={chatStarted}>
            <ChatHeading chatStarted={chatStarted}>
              {chatHeading && chatHeading}
            </ChatHeading>
            <ChatContent isOpen={isSidebarOpen} chatStarted={chatStarted}>
              {chatContent && chatContent}
            </ChatContent>
          </ChatScrollArea>
        </ContentWrapper>
      </LayoutWrapper>
      <ChatBox chatStarted={chatStarted} sidebarOpen={isSidebarOpen}>
        {chatComponent && chatComponent}
      </ChatBox>
      <InputBox chatStarted={chatStarted} sidebarOpen={isSidebarOpen}>
        {inputBox && inputBox}
      </InputBox>
      <OverlayHide />

      <Popup
        content="Scroll to bottom"
        inverted
        position="top center"
        trigger={
          <ScrollToBottomButton
            show={showScrollToBottom}
            sidebarOpen={isSidebarOpen}
            onClick={scrollToBottom}
          >
            <Icon
              name={ICON_SET.dropdown}
              size={20}
              style={{ cursor: "pointer", backgroundColor: "white", opacity: 1, zIndex: 1000 }}
            />
          </ScrollToBottomButton>
        }
      />
    </BaseTemplate>
  );
};

ChatTemplate.propTypes = {
  heading: PropTypes.string,
  pageTitle: PropTypes.string,
  subtitle: PropTypes.string,
  onSend: PropTypes.func,
  scrollable: PropTypes.bool,
  chatHeading: PropTypes.node,
  chatContent: PropTypes.node,
  chatStarted: PropTypes.bool,
  chatComponent: PropTypes.node,
  submenuData: PropTypes.shape({
    sectionOne: PropTypes.node,
    sectionTwo: PropTypes.node,
  }),
  fromPulseSurvey: PropTypes.bool,
};

ChatTemplate.defaultProps = {
  scrollable: false,
  fromPulseSurvey: false,
};

export default ChatTemplate;